import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Heart, ArrowLeft, Plus, Sparkles, Edit, Trash2, Wand2, Filter, Target, Package, Share2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { AIErrorInline } from '@/components/AIUnavailableNotice';
import AIFeedbackPopup, { shouldShowFeedback } from '@/components/AIFeedbackPopup';
import { shareActivityPdf } from '@/lib/shareActivityPdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const adaptLanguageForStage = (examples, stage) => {
  if (!examples || examples.length === 0) return [];
  
  if (stage === 1) {
    return examples.filter(ex => ex.split(' ').length >= 2);
  } else if (stage === 2) {
    const baseGestalts = examples.filter(ex => ex.split(' ').length >= 2);
    const mitigated = [];
    for (let gestalt of baseGestalts.slice(0, 2)) {
      const words = gestalt.split(' ');
      if (words.length >= 2) {
        mitigated.push(`${words[0]} ${words[1]}...`);
      }
    }
    return [...baseGestalts.slice(0, 2), ...mitigated];
  } else if (stage === 3) {
    const singleWords = new Set();
    examples.forEach(ex => {
      const words = ex.replace(/[!,]/g, '').split(' ');
      words.forEach(w => {
        if (w.length > 2) singleWords.add(w.toLowerCase());
      });
    });
    return Array.from(singleWords).slice(0, 5);
  } else if (stage === 4) {
    const twoWord = new Set();
    examples.forEach(ex => {
      const words = ex.replace(/[!,]/g, '').split(' ');
      if (words.length >= 2) {
        twoWord.add(`${words[0]} ${words[1]}`);
      }
    });
    return Array.from(twoWord).slice(0, 5);
  } else {
    return examples;
  }
};

const getStageGuidance = (stage) => {
  const guidance = {
    1: 'Model complete gestalts consistently',
    2: 'Continue full phrases; celebrate mixing',
    3: 'Emphasize single words from gestalts',
    4: 'Support 2-word combinations',
    5: 'Model longer grammatical phrases',
    6: 'Use complex sentences'
  };
  return guidance[stage] || '';
};

export default function ActivityLibrary({ onLogout }) {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [editActivity, setEditActivity] = useState(null);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [showAIFeedback, setShowAIFeedback] = useState(false);
  const [childContext, setChildContext] = useState(null);
  const [newlyCreatedIds, setNewlyCreatedIds] = useState([]);
  const [showSimilarDialog, setShowSimilarDialog] = useState(false);
  const [similarActivities, setSimilarActivities] = useState([]);
  const [pendingAiRequest, setPendingAiRequest] = useState(null);
  const [availableGoals, setAvailableGoals] = useState([]);
  const [selectedChildId, setSelectedChildId] = useState('');
  const [childrenList, setChildrenList] = useState([]);
  const [filters, setFilters] = useState({
    regulation_level: 'all',
    communication_function: 'all',
    goal_area: 'all'
  });
  const [sharingActivityId, setSharingActivityId] = useState(null);
  const [aiFormData, setAiFormData] = useState({
    child_interests: '',
    communication_style: 'GLP',
    regulation_preference: 'none',
    selected_goals: [],
    custom_goal: '',
    materials: '',
    age: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    play_type: '',
    communication_functions: '',
    regulation_level: 'calm',
    language_examples: '',
    follow_lead_guidance: '',
    notes: ''
  });

  // Goal area keyword matching for library filtering
  const GOAL_AREA_KEYWORDS = {
    language: ['language', 'expressive', 'receptive', 'vocabulary', 'gestalt', 'sentence', 'grammar', 'word', 'phrase', 'requesting', 'commenting', 'labeling', 'describing', 'narrating', 'predicting'],
    literacy: ['literacy', 'reading', 'writing', 'book', 'letter', 'phonics', 'story', 'print', 'rhyme', 'syllable'],
    speech: ['speech', 'articulation', 'phonolog', 'sound', 'pronunciation', 'motor speech', 'apraxia', 'dysarthria'],
    'oral-motor': ['oral motor', 'oral-motor', 'chewing', 'blowing', 'mouth', 'lip', 'tongue', 'jaw', 'feeding', 'swallow'],
    'auditory-processing': ['auditory', 'listening', 'sound discrimination', 'auditory processing', 'hearing', 'phonemic'],
    'social-communication': ['social', 'pragmatic', 'turn.taking', 'conversation', 'peer', 'joint attention', 'eye contact', 'greet', 'initiat'],
    aac: ['aac', 'augmentative', 'alternative communication', 'symbol', 'device', 'core word', 'aided language', 'visual support', 'picture exchange'],
    fluency: ['fluency', 'stutter', 'stammer', 'disfluency', 'smooth speech', 'easy onset', 'pacing']
  };

  const matchesGoalArea = (activity, goalArea) => {
    if (!goalArea || goalArea === 'all') return true;
    const keywords = GOAL_AREA_KEYWORDS[goalArea] || [];
    const text = [
      activity.name,
      activity.notes,
      activity.play_type,
      ...(activity.communication_functions || []),
      ...(activity.language_examples || []),
      activity.follow_lead_guidance
    ].filter(Boolean).join(' ').toLowerCase();
    return keywords.some(kw => text.includes(kw));
  };

  // Sort activities: newly created at top, then alphabetically
  const sortedActivities = [...activities]
    .filter(a => matchesGoalArea(a, filters.goal_area))
    .sort((a, b) => {
    const aIsNew = newlyCreatedIds.includes(a.id);
    const bIsNew = newlyCreatedIds.includes(b.id);
    
    if (aIsNew && !bIsNew) return -1;
    if (!aIsNew && bIsNew) return 1;
    if (aIsNew && bIsNew) {
      // Both new - most recent first
      return newlyCreatedIds.indexOf(a.id) - newlyCreatedIds.indexOf(b.id);
    }
    // Both not new - alphabetical
    return a.name.localeCompare(b.name);
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const childId = params.get('childId');
    if (childId) {
      fetchChildContext(childId);
      setSelectedChildId(childId);
      fetchGoalsForChild(childId);
    }
    fetchActivities();
    fetchChildren();
  }, [filters]);

  const fetchChildren = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/children`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChildrenList(response.data);
    } catch (error) {
      console.error('Failed to load children');
    }
  };

  const fetchGoalsForChild = async (childId) => {
    if (!childId) {
      setAvailableGoals([]);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/children/${childId}/all-goals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableGoals(response.data.goals || []);
    } catch (error) {
      console.error('Failed to load goals');
      setAvailableGoals([]);
    }
  };

  const handleChildSelect = (childId) => {
    setSelectedChildId(childId);
    fetchGoalsForChild(childId);
    if (childId) {
      fetchChildContext(childId);
    } else {
      setChildContext(null);
    }
    // Reset selected goals and age when child changes
    setAiFormData(prev => ({ ...prev, selected_goals: [], age: '' }));
  };

  // When child context loads, pre-fill age and communication style
  const onChildContextLoaded = (context) => {
    if (context) {
      setAiFormData(prev => ({
        ...prev,
        age: context.age ? String(context.age) : '',
        communication_style: context.communication_style || context.language_learner_profile || 'GLP'
      }));
    }
  };

  const fetchChildContext = async (childId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/children/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChildContext(response.data);
      onChildContextLoaded(response.data);
    } catch (error) {
      console.error('Failed to load child context');
    }
  };

  const fetchActivities = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filters.regulation_level && filters.regulation_level !== 'all') {
        params.append('regulation_level', filters.regulation_level);
      }
      if (filters.communication_function && filters.communication_function !== 'all') {
        params.append('communication_function', filters.communication_function);
      }
      
      const response = await axios.get(`${API}/activities?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivities(response.data);
    } catch (error) {
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  // Check for similar activities before AI generation
  const findSimilarActivities = (interests, playType) => {
    const interestWords = interests.toLowerCase().split(/[,\s]+/).filter(w => w.length > 2);
    
    return activities.filter(activity => {
      const activityText = `${activity.name} ${activity.play_type} ${activity.notes || ''}`.toLowerCase();
      const matchScore = interestWords.filter(word => activityText.includes(word)).length;
      return matchScore >= 1; // At least one interest word matches
    }).slice(0, 5); // Return top 5 similar
  };

  const handleAIGenerate = async (e) => {
    e.preventDefault();
    
    // First check for similar existing activities
    const similar = findSimilarActivities(aiFormData.child_interests, '');
    
    if (similar.length > 0) {
      setSimilarActivities(similar);
      setPendingAiRequest(aiFormData);
      setShowSimilarDialog(true);
      return;
    }
    
    // No similar activities, proceed with generation
    await generateNewActivity(aiFormData);
  };

  const generateNewActivity = async (requestData) => {
    setAiGenerating(true);
    setShowSimilarDialog(false);
    setAiError(null); // Clear any previous error
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        child_interests: requestData.child_interests ? requestData.child_interests.split(',').map(i => i.trim()).filter(i => i) : [],
        communication_style: requestData.communication_style,
        regulation_preference: requestData.regulation_preference === 'none' ? null : requestData.regulation_preference,
        goals: requestData.selected_goals || [],
        custom_goal: requestData.custom_goal || '',
        materials: requestData.materials || '',
        child_id: selectedChildId || null,
        glp_stage: childContext?.glp_stage || null,
        child_age: requestData.age ? parseInt(requestData.age) : (childContext?.age || null)
      };
      
      const response = await axios.post(`${API}/activities/generate-ai`, payload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Track newly created activity
      setNewlyCreatedIds(prev => [response.data.id, ...prev]);
      
      toast.success(`Created: ${response.data.name}! (Added to top)`);
      
      // Check for usage warning
      if (response.data.usage_warning) {
        const warning = response.data.usage_warning;
        const info = response.data.usage_info;
        
        if (warning.level === 'limit_reached') {
          toast.error(warning.message, { duration: 8000 });
        } else if (warning.level === 'critical') {
          toast.warning(`⚠️ ${warning.message}`, { duration: 6000 });
        } else if (warning.level === 'warning') {
          toast.info(`📊 ${warning.message}`, { duration: 5000 });
        }
      }
      
      setShowAIDialog(false);
      if (shouldShowFeedback('activity_generation')) {
        setShowAIFeedback(true);
      }
      fetchActivities();
      setAiFormData({
        child_interests: '',
        communication_style: 'GLP',
        regulation_preference: 'none',
        selected_goals: [],
        custom_goal: '',
        materials: '',
        age: ''
      });
    } catch (error) {
      console.error('AI generation error:', error);
      const statusCode = error.response?.status;
      const errorDetail = error.response?.data?.detail || error.message || 'Failed to generate activity';
      
      // Check if it's an AI service unavailability issue (500 errors, rate limits, or token issues)
      if (statusCode === 500 || statusCode === 503 || statusCode === 429 || 
          errorDetail.toLowerCase().includes('token') || 
          errorDetail.toLowerCase().includes('rate') ||
          errorDetail.toLowerCase().includes('unavailable') ||
          errorDetail.toLowerCase().includes('api')) {
        setAiError('AI generation is temporarily unavailable. Please try again in a few minutes.');
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setAiGenerating(false);
    }
  };

  const selectSimilarActivity = (activity) => {
    // Add the selected similar activity to newly created so it shows at top
    setNewlyCreatedIds(prev => [activity.id, ...prev]);
    toast.success(`"${activity.name}" added to top of your library!`);
    setShowSimilarDialog(false);
    setShowAIDialog(false);
    setPendingAiRequest(null);
  };

  const clearFilters = () => {
    setFilters({
      regulation_level: 'all',
      communication_function: 'all',
      goal_area: 'all'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        communication_functions: formData.communication_functions.split(',').map(f => f.trim()),
        language_examples: formData.language_examples.split('\n').filter(l => l.trim())
      };

      if (editActivity) {
        await axios.put(`${API}/activities/${editActivity.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Activity updated!');
      } else {
        const response = await axios.post(`${API}/activities`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Track newly created activity
        setNewlyCreatedIds(prev => [response.data.id, ...prev]);
        toast.success('Activity created! (Added to top)');
      }

      setShowDialog(false);
      setEditActivity(null);
      fetchActivities();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save activity');
    }
  };

  const handleEdit = (activity) => {
    setEditActivity(activity);
    setFormData({
      name: activity.name,
      play_type: activity.play_type,
      communication_functions: activity.communication_functions.join(', '),
      regulation_level: activity.regulation_level,
      language_examples: activity.language_examples.join('\n'),
      follow_lead_guidance: activity.follow_lead_guidance,
      notes: activity.notes
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this activity?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/activities/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Activity deleted');
      fetchActivities();
    } catch (error) {
      toast.error('Failed to delete activity');
    }
  };

  const handleShare = async (activity) => {
    if (sharingActivityId) return;
    setSharingActivityId(activity.id);
    try {
      // If a child is in context (e.g. opened from a child profile), pass it
      // so the backend overlays the therapist's customised version.
      const childId = childContext?.id || selectedChildId || undefined;
      const childFirstName = childContext?.name?.split(' ')[0];
      const result = await shareActivityPdf({
        activityId: activity.id,
        childId,
        activityName: activity.name,
        childFirstName,
      });
      if (result === 'shared') {
        toast.success('Shared!');
      } else if (result === 'downloaded') {
        toast.success('PDF downloaded — attach it to your message.');
      } else if (result === 'cancelled') {
        // user dismissed the share sheet — stay silent
      } else {
        toast.error("Couldn't prepare the PDF. Try again in a moment.");
      }
    } finally {
      setSharingActivityId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      play_type: '',
      communication_functions: '',
      regulation_level: 'calm',
      language_examples: '',
      follow_lead_guidance: '',
      notes: ''
    });
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              data-testid="back-btn"
              onClick={() => navigate(-1)}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-2xl font-serif text-primary-900">PlayFirst</h1>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Community Sharing Banner */}
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-4 mb-6 border border-purple-100" data-testid="community-banner">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-purple-900 mb-1">Community-Powered Library</h3>
                <p className="text-sm text-purple-700">
                  Every activity you add helps grow our shared library for all PlayFirst therapists! 
                  Browse activities created by fellow speech therapists, or add your own favorites. 
                  Together, we're building the largest GLP-informed activity bank.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6 mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-4xl md:text-5xl font-serif font-normal text-primary-900 mb-2">
                  Activity library
                </h2>
                <p className="text-lg text-stone-600">
                  {childContext 
                    ? `Activities adapted for ${childContext.name}${childContext.glp_stage ? ` (Stage ${childContext.glp_stage})` : ''}`
                    : 'Play-based activities for connection'
                  }
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  data-testid="toggle-filters-btn"
                  onClick={() => setShowFilters(!showFilters)}
                  className="rounded-full"
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                </Button>
                
                <Dialog open={showAIDialog} onOpenChange={setShowAIDialog}>
                  <DialogTrigger asChild>
                    <Button
                      data-testid="ai-generate-btn"
                      variant="outline"
                      className="rounded-full border-purple-200 text-purple-700 hover:bg-purple-50"
                    >
                      <Wand2 className="w-4 h-4 mr-2" />
                      AI Generate
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-dialog">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-serif">Generate AI Activity</DialogTitle>
                      <p className="text-sm text-stone-600 mt-2">
                        Create a custom activity based on interests, goals, and/or materials - use any combination!
                      </p>
                    </DialogHeader>
                    <form onSubmit={handleAIGenerate} className="space-y-4 mt-4">
                      {/* Child Selection (optional) */}
                      {childrenList.length > 0 && (
                        <div className="space-y-2">
                          <Label htmlFor="ai-child-select">Select child (optional - to load their goals)</Label>
                          <Select
                            value={selectedChildId || "none"}
                            onValueChange={(value) => handleChildSelect(value === "none" ? "" : value)}
                          >
                            <SelectTrigger data-testid="ai-child-select" className="h-12 rounded-2xl">
                              <SelectValue placeholder="Select a child..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No specific child</SelectItem>
                              {childrenList.map(child => (
                                <SelectItem key={child.id} value={child.id}>{child.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Interests Field */}
                      <div className="space-y-2">
                        <Label htmlFor="ai-interests">Child's interests (comma-separated)</Label>
                        <Input
                          id="ai-interests"
                          data-testid="ai-interests-input"
                          placeholder="pool pumps, washing machines, trains"
                          value={aiFormData.child_interests}
                          onChange={(e) => setAiFormData({ ...aiFormData, child_interests: e.target.value })}
                          className="h-12 rounded-2xl"
                        />
                        <p className="text-xs text-stone-500">Even unconventional interests work!</p>
                      </div>

                      {/* Goals Section */}
                      <div className="space-y-3 p-4 bg-purple-50/50 rounded-2xl border border-purple-100">
                        <div className="flex items-center gap-2 mb-2">
                          <Target className="w-4 h-4 text-purple-600" />
                          <Label className="text-purple-900 font-medium">Therapy Goals</Label>
                        </div>
                        
                        {/* Goals from child's profile */}
                        {availableGoals.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-xs text-purple-700">Select from existing goals:</p>
                            <div className="max-h-32 overflow-y-auto space-y-2 pr-2">
                              {availableGoals.map((goal, index) => (
                                <div key={index} className="flex items-start gap-2">
                                  <Checkbox
                                    id={`goal-${index}`}
                                    data-testid={`goal-checkbox-${index}`}
                                    checked={aiFormData.selected_goals.includes(goal)}
                                    onCheckedChange={(checked) => {
                                      if (checked) {
                                        setAiFormData(prev => ({
                                          ...prev,
                                          selected_goals: [...prev.selected_goals, goal]
                                        }));
                                      } else {
                                        setAiFormData(prev => ({
                                          ...prev,
                                          selected_goals: prev.selected_goals.filter(g => g !== goal)
                                        }));
                                      }
                                    }}
                                    className="mt-0.5"
                                  />
                                  <label htmlFor={`goal-${index}`} className="text-sm text-stone-700 cursor-pointer leading-tight">
                                    {goal}
                                  </label>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {availableGoals.length === 0 && selectedChildId && (
                          <p className="text-xs text-purple-600 italic">No goals found for this child. You can type a custom goal below.</p>
                        )}
                        
                        {!selectedChildId && (
                          <p className="text-xs text-purple-600 italic">Select a child above to see their goals, or type a custom goal below.</p>
                        )}

                        {/* Custom goal input */}
                        <div className="space-y-1 pt-2">
                          <Label htmlFor="ai-custom-goal" className="text-xs text-purple-700">Or enter a custom goal:</Label>
                          <Input
                            id="ai-custom-goal"
                            data-testid="ai-custom-goal-input"
                            placeholder="e.g., Increase use of requesting functions"
                            value={aiFormData.custom_goal}
                            onChange={(e) => setAiFormData({ ...aiFormData, custom_goal: e.target.value })}
                            className="h-10 rounded-xl bg-white"
                          />
                        </div>
                      </div>

                      {/* Materials Field */}
                      <div className="space-y-2 p-4 bg-amber-50/50 rounded-2xl border border-amber-100">
                        <div className="flex items-center gap-2 mb-1">
                          <Package className="w-4 h-4 text-amber-600" />
                          <Label htmlFor="ai-materials" className="text-amber-900 font-medium">Available Materials</Label>
                        </div>
                        <Textarea
                          id="ai-materials"
                          data-testid="ai-materials-input"
                          placeholder="e.g., bubbles, playdough, sensory bin, toy cars, blocks..."
                          value={aiFormData.materials}
                          onChange={(e) => setAiFormData({ ...aiFormData, materials: e.target.value })}
                          className="min-h-16 rounded-xl bg-white resize-none"
                        />
                        <p className="text-xs text-amber-600">What materials do you have available for the session?</p>
                      </div>

                      {/* Communication Style */}
                      <div className="space-y-2">
                        <Label htmlFor="ai-communication">Communication style</Label>
                        <Select
                          value={aiFormData.communication_style}
                          onValueChange={(value) => setAiFormData({ ...aiFormData, communication_style: value })}
                        >
                          <SelectTrigger data-testid="ai-communication-select" className="h-12 rounded-2xl">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="GLP">GLP (Gestalt Language Processing)</SelectItem>
                            <SelectItem value="Analytic">Analytic</SelectItem>
                            <SelectItem value="AAC">AAC (Augmentative Communication)</SelectItem>
                            <SelectItem value="Mixed">Mixed</SelectItem>
                          </SelectContent>
                        </Select>
                        {childContext?.glp_stage && (
                          <p className="text-xs text-purple-600">
                            GLP Stage {childContext.glp_stage} detected — language models will match this stage
                          </p>
                        )}
                      </div>

                      {/* Age & Regulation Row */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {/* Age (NT reference) */}
                        <div className="space-y-2">
                          <Label>Age (for neurotypical developmental reference)</Label>
                          <Input
                            type="number"
                            min="1"
                            max="18"
                            placeholder="e.g., 4"
                            value={aiFormData.age}
                            onChange={(e) => setAiFormData({ ...aiFormData, age: e.target.value })}
                            className="h-10 rounded-xl"
                            data-testid="ai-age-input"
                          />
                          <p className="text-xs text-stone-400">Only needed for neurotypical clients — neurodiverse children follow their own communication timeline</p>
                        </div>

                        {/* Regulation Preference */}
                        <div className="space-y-2">
                          <Label>Regulation preference</Label>
                          <Select
                            value={aiFormData.regulation_preference || 'none'}
                            onValueChange={(value) => setAiFormData({ ...aiFormData, regulation_preference: value })}
                          >
                            <SelectTrigger data-testid="ai-regulation-select" className="h-10 rounded-xl">
                              <SelectValue placeholder="Any" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Any</SelectItem>
                              <SelectItem value="calming">Calming / Low arousal</SelectItem>
                              <SelectItem value="alerting">Alerting / High energy</SelectItem>
                              <SelectItem value="organizing">Organizing / Structured</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {aiError && (
                        <AIErrorInline 
                          message={aiError} 
                          onRetry={() => {
                            setAiError(null);
                            handleAIGenerate({ preventDefault: () => {} });
                          }} 
                        />
                      )}

                      <div className="bg-stone-50 rounded-xl p-3 text-xs text-stone-600">
                        <strong>Tip:</strong> You can use any combination of interests, goals, and materials. The AI will create an activity based on whatever you provide!
                      </div>

                      <Button
                        type="submit"
                        data-testid="generate-ai-activity-btn"
                        disabled={aiGenerating || (!aiFormData.child_interests && !aiFormData.custom_goal && aiFormData.selected_goals.length === 0 && !aiFormData.materials)}
                        className="w-full h-12 rounded-full"
                      >
                        {aiGenerating ? 'Generating...' : 'Generate Activity'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
                
                <Dialog
                  open={showDialog}
                  onOpenChange={(open) => {
                    setShowDialog(open);
                    if (!open) {
                      setEditActivity(null);
                      resetForm();
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <Button data-testid="add-activity-btn" className="rounded-full shadow-lg shadow-primary/20">
                      <Plus className="w-4 h-4 mr-2" />
                      Add activity
                    </Button>
                  </DialogTrigger>
              <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="activity-dialog">
                <DialogHeader>
                  <DialogTitle className="text-2xl font-serif">
                    {editActivity ? 'Edit activity' : 'Create activity'}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label htmlFor="activity-name">Activity name</Label>
                    <Input
                      id="activity-name"
                      data-testid="activity-name-input"
                      placeholder="Bubble play"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="h-12 rounded-2xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="play-type">Play type</Label>
                    <Input
                      id="play-type"
                      data-testid="play-type-input"
                      placeholder="Sensory play"
                      value={formData.play_type}
                      onChange={(e) => setFormData({ ...formData, play_type: e.target.value })}
                      required
                      className="h-12 rounded-2xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="communication-functions">Communication functions (comma-separated)</Label>
                    <Input
                      id="communication-functions"
                      data-testid="communication-functions-input"
                      placeholder="requesting, protesting, commenting"
                      value={formData.communication_functions}
                      onChange={(e) => setFormData({ ...formData, communication_functions: e.target.value })}
                      className="h-12 rounded-2xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="regulation-level">Regulation level</Label>
                    <Select
                      value={formData.regulation_level}
                      onValueChange={(value) => setFormData({ ...formData, regulation_level: value })}
                    >
                      <SelectTrigger data-testid="regulation-level-select" className="h-12 rounded-2xl">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="calm">Calm</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="sensory">Sensory</SelectItem>
                        <SelectItem value="flexible">Flexible</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="language-examples">Language to model (one per line)</Label>
                    <Textarea
                      id="language-examples"
                      data-testid="language-examples-input"
                      placeholder="Pop pop pop!\nBig bubble\nMore bubbles"
                      value={formData.language_examples}
                      onChange={(e) => setFormData({ ...formData, language_examples: e.target.value })}
                      className="rounded-2xl min-h-24"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="follow-lead">Follow the child's lead guidance</Label>
                    <Textarea
                      id="follow-lead"
                      data-testid="follow-lead-input"
                      placeholder="Watch for eye gaze and pointing. Let them choose when to pop bubbles..."
                      value={formData.follow_lead_guidance}
                      onChange={(e) => setFormData({ ...formData, follow_lead_guidance: e.target.value })}
                      className="rounded-2xl min-h-24"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes (optional)</Label>
                    <Textarea
                      id="notes"
                      data-testid="notes-input"
                      placeholder="Additional notes for therapists..."
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="rounded-2xl min-h-20"
                    />
                  </div>
                  <Button type="submit" data-testid="save-activity-btn" className="w-full h-12 rounded-full">
                    {editActivity ? 'Save changes' : 'Create activity'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>
        </div>

        {showFilters && (
            <Card className="p-6 bg-white rounded-3xl border border-stone-100 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-primary-900">Filter Activities</h3>
                {(filters.regulation_level !== 'all' || filters.communication_function !== 'all' || filters.goal_area !== 'all') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearFilters}
                    data-testid="clear-filters-btn"
                    className="rounded-full"
                  >
                    Clear filters
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="filter-goal-area">Goal area</Label>
                  <Select
                    value={filters.goal_area}
                    onValueChange={(value) => setFilters({ ...filters, goal_area: value })}
                  >
                    <SelectTrigger id="filter-goal-area" data-testid="filter-goal-area-select" className="h-12 rounded-2xl">
                      <SelectValue placeholder="All areas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All areas</SelectItem>
                      <SelectItem value="language">Language</SelectItem>
                      <SelectItem value="literacy">Literacy</SelectItem>
                      <SelectItem value="speech">Speech (Articulation)</SelectItem>
                      <SelectItem value="oral-motor">Oral Motor</SelectItem>
                      <SelectItem value="auditory-processing">Auditory Processing</SelectItem>
                      <SelectItem value="social-communication">Social Communication</SelectItem>
                      <SelectItem value="aac">AAC</SelectItem>
                      <SelectItem value="fluency">Fluency</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="filter-regulation">Regulation level</Label>
                  <Select
                    value={filters.regulation_level}
                    onValueChange={(value) => setFilters({ ...filters, regulation_level: value })}
                  >
                    <SelectTrigger id="filter-regulation" data-testid="filter-regulation-select" className="h-12 rounded-2xl">
                      <SelectValue placeholder="All levels" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All levels</SelectItem>
                      <SelectItem value="calm">Calm</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="sensory">Sensory</SelectItem>
                      <SelectItem value="flexible">Flexible</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="filter-communication">Communication function</Label>
                  <Select
                    value={filters.communication_function}
                    onValueChange={(value) => setFilters({ ...filters, communication_function: value })}
                  >
                    <SelectTrigger id="filter-communication" data-testid="filter-communication-select" className="h-12 rounded-2xl">
                      <SelectValue placeholder="All functions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All functions</SelectItem>
                      <SelectItem value="requesting">Requesting</SelectItem>
                      <SelectItem value="protesting">Protesting</SelectItem>
                      <SelectItem value="commenting">Commenting</SelectItem>
                      <SelectItem value="labeling">Labeling</SelectItem>
                      <SelectItem value="describing">Describing</SelectItem>
                      <SelectItem value="predicting">Predicting</SelectItem>
                      <SelectItem value="narrating">Narrating</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {(filters.regulation_level !== 'all' || filters.communication_function !== 'all' || filters.goal_area !== 'all') && (
                <div className="mt-4 text-sm text-stone-600">
                  Showing activities
                  {filters.goal_area !== 'all' && ` in ${filters.goal_area.replace('-', ' ')}`}
                  {filters.regulation_level !== 'all' && ` with ${filters.regulation_level} regulation level`}
                  {filters.communication_function !== 'all' && ` supporting ${filters.communication_function}`}
                </div>
              )}
            </Card>
          )}

          {loading ? (
            <div className="text-center py-12">
              <p className="text-stone-600">Loading...</p>
            </div>
          ) : activities.length === 0 ? (
            <Card className="p-12 text-center bg-white rounded-3xl border border-stone-100" data-testid="empty-state">
              <Sparkles className="w-16 h-16 mx-auto mb-4 text-primary/50" />
              <h3 className="text-xl font-medium text-stone-700 mb-2">No activities yet</h3>
              <p className="text-stone-500 mb-6">Create your first play-based activity</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {showAIFeedback && (
                <div className="col-span-full mb-2">
                  <AIFeedbackPopup type="activity_generation" context="Activity Library AI Generator" onDismiss={() => setShowAIFeedback(false)} />
                </div>
              )}
              {sortedActivities.map((activity, index) => {
                const isNewlyCreated = newlyCreatedIds.includes(activity.id);
                return (
                <motion.div
                  key={activity.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className={`p-6 rounded-3xl border hover:shadow-md transition-all ${isNewlyCreated ? 'bg-green-50 border-green-200' : 'bg-white border-stone-100'}`} data-testid={`activity-card-${activity.id}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="text-xl font-serif text-primary-900">{activity.name}</h3>
                          {isNewlyCreated && (
                            <span className="px-2 py-0.5 bg-green-200 text-green-800 text-xs rounded-full font-medium">
                              New
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-stone-500">{activity.play_type}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`share-activity-btn-${activity.id}`}
                          onClick={() => handleShare(activity)}
                          disabled={sharingActivityId === activity.id}
                          className="rounded-full h-8 w-8 p-0 text-primary hover:text-primary"
                          aria-label="Share with parent"
                          title="Share with parent"
                        >
                          <Share2 className={`w-4 h-4 ${sharingActivityId === activity.id ? 'animate-pulse' : ''}`} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`edit-activity-btn-${activity.id}`}
                          onClick={() => handleEdit(activity)}
                          className="rounded-full h-8 w-8 p-0"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`delete-activity-btn-${activity.id}`}
                          onClick={() => handleDelete(activity.id)}
                          className="rounded-full h-8 w-8 p-0 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <p className="text-xs font-bold tracking-wide uppercase text-stone-400 mb-1">Regulation</p>
                        <span className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full">
                          {activity.regulation_level}
                        </span>
                      </div>

                      {activity.communication_functions.length > 0 && (
                        <div>
                          <p className="text-xs font-bold tracking-wide uppercase text-stone-400 mb-2">Functions</p>
                          <div className="flex flex-wrap gap-2">
                            {activity.communication_functions.map((func, i) => (
                              <span key={i} className="px-2 py-1 bg-stone-100 text-stone-700 text-xs rounded-full">
                                {func}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {activity.language_examples.length > 0 && (
                        <div>
                          <p className="text-xs font-bold tracking-wide uppercase text-stone-400 mb-2">
                            Language examples
                            {childContext?.glp_stage && (
                              <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full normal-case">
                                Stage {childContext.glp_stage} adapted
                              </span>
                            )}
                          </p>
                          <div className="space-y-1">
                            {(childContext?.glp_stage 
                              ? adaptLanguageForStage(activity.language_examples, childContext.glp_stage)
                              : activity.language_examples
                            ).slice(0, 3).map((example, i) => (
                              <p key={i} className="text-sm text-stone-600 italic">"{example}"</p>
                            ))}
                          </div>
                          {childContext?.glp_stage && (
                            <p className="text-xs text-purple-600 mt-2">
                              {getStageGuidance(childContext.glp_stage)}
                            </p>
                          )}
                        </div>
                      )}

                      <div>
                        <p className="text-xs font-bold tracking-wide uppercase text-stone-400 mb-1">Follow their lead</p>
                        <p className="text-sm text-stone-600 line-clamp-2">{activity.follow_lead_guidance}</p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
              })}
            </div>
          )}
        </motion.div>
      </main>
      
      {/* Similar Activities Dialog */}
      <Dialog open={showSimilarDialog} onOpenChange={setShowSimilarDialog}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="similar-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">Similar activities found!</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-stone-600">
              We found existing activities that match your interests. Would you like to use one of these instead?
            </p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {similarActivities.map(activity => (
                <button
                  key={activity.id}
                  onClick={() => selectSimilarActivity(activity)}
                  className="w-full p-3 bg-stone-50 rounded-xl text-left hover:bg-stone-100 transition-colors"
                >
                  <p className="font-medium text-primary-900">{activity.name}</p>
                  <p className="text-xs text-stone-500">{activity.play_type}</p>
                </button>
              ))}
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowSimilarDialog(false)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                onClick={() => generateNewActivity(pendingAiRequest)}
                disabled={aiGenerating}
                className="flex-1 rounded-full"
              >
                {aiGenerating ? 'Generating...' : 'Generate New Anyway'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
